function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Replicates the key-normalization Spectrum applies to listbox option ids
 * (see https://github.com/adobe/react-spectrum/blob/9508b15bf8c0e968c56220548207cc57c7e4f57c/packages/react-aria/src/listbox/utils.ts#L31). Whitespace is stripped so that
 * `<listId>-option-<normalizedKey>` matches the actual rendered DOM `id`.
 */
function normalizeKey(key) {
  return key.replace(/\s+/g, '');
}

/**
 * Owns virtual-focus tracking, keyboard handling, and the DOM/scroll
 * side-effects for option highlighting in MultiSelect. Keyboard navigation
 * is handled outside Spectrum's ListBox because the input retains real
 * focus while options are visually highlighted via `data-dh-focused`.
 *
 * Focus is tracked by item key, so it survives filtering, virtualization, etc
 * where the underlying filteredItems array shifts independently of the user's
 * intended focus target.
 */
export function useMultiSelectKeyboard(_ref) {
  var filteredItems = _ref.filteredItems,
    allItems = _ref.allItems,
    shouldFocusWrap = _ref.shouldFocusWrap,
    overlayState = _ref.overlayState,
    openOverlay = _ref.openOverlay,
    closeOverlay = _ref.closeOverlay,
    isReadOnly = _ref.isReadOnly,
    isDisabled = _ref.isDisabled,
    searchText = _ref.searchText,
    setSearchText = _ref.setSearchText,
    selectedKeys = _ref.selectedKeys,
    toggleKey = _ref.toggleKey,
    allowsCustomValue = _ref.allowsCustomValue,
    menuTrigger = _ref.menuTrigger,
    onKeyDown = _ref.onKeyDown,
    listBoxContainerRef = _ref.listBoxContainerRef,
    inputRef = _ref.inputRef;
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    focusedKey = _useState2[0],
    setFocusedKey = _useState2[1];
  var moveFocus = useCallback(dir => {
    var len = filteredItems.length;
    if (len === 0) {
      return;
    }
    setFocusedKey(prev => {
      var currentIdx = prev == null ? -1 : filteredItems.findIndex(i => i.key === prev);
      var nextIdx;
      if (dir === 'down') {
        if (currentIdx === -1) {
          nextIdx = 0;
        } else if (shouldFocusWrap) {
          nextIdx = (currentIdx + 1) % len;
        } else {
          nextIdx = Math.min(currentIdx + 1, len - 1);
        }
      } else if (currentIdx === -1) {
        nextIdx = len - 1;
      } else if (shouldFocusWrap) {
        nextIdx = (currentIdx - 1 + len) % len;
      } else {
        nextIdx = Math.max(currentIdx - 1, 0);
      }
      return filteredItems[nextIdx].key;
    });
  }, [filteredItems, shouldFocusWrap]);
  var handleInputKeyDown = useCallback(e => {
    if (isDisabled) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!overlayState.isOpen) {
          openOverlay('manual');
        } else {
          moveFocus('down');
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (overlayState.isOpen) {
          moveFocus('up');
        }
        break;
      case 'Enter':
        {
          if (!overlayState.isOpen) {
            e.preventDefault();
            openOverlay('manual');
            break;
          }
          var focusedItemStillExists = focusedKey != null && filteredItems.some(i => i.key === focusedKey);
          if (!isReadOnly && focusedItemStillExists) {
            e.preventDefault();
            toggleKey(focusedKey);
          } else if (!isReadOnly && allowsCustomValue && searchText.trim() !== '') {
            e.preventDefault();
            var trimmed = searchText.trim();
            // Check if typed text exactly matches an existing item label
            var matchingItem = allItems.find(item => item.label.toLowerCase() === trimmed.toLowerCase());
            toggleKey(matchingItem != null ? matchingItem.key : trimmed);
            setSearchText('');
          } else {
            // No focused item, no custom value — clear search and close
            setSearchText('');
            closeOverlay();
          }
          break;
        }
      case 'Escape':
        if (overlayState.isOpen) {
          e.preventDefault();
          closeOverlay();
        }
        break;
      case 'Tab':
        if (overlayState.isOpen) {
          closeOverlay();
        }
        break;
      case 'Backspace':
        if (searchText === '' && !isReadOnly && selectedKeys.size > 0) {
          var keys = [...selectedKeys];
          var lastKey = keys[keys.length - 1];
          if (lastKey != null) {
            toggleKey(lastKey);
          }
        }
        break;
      default:
        break;
    }
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
  }, [isDisabled, overlayState, openOverlay, closeOverlay, moveFocus, isReadOnly, focusedKey, filteredItems, allItems, allowsCustomValue, toggleKey, searchText, setSearchText, selectedKeys, onKeyDown]);

  // Reset state only when the dropdown closes (not every render). This avoids clearing the input
  // on each keystroke, especially in menuTrigger='manual' mode.
  var wasOpenRef = useRef(false);
  useEffect(() => {
    if (wasOpenRef.current && !overlayState.isOpen) {
      if (!allowsCustomValue) {
        setSearchText('');
      }
      setFocusedKey(null);
    }
    wasOpenRef.current = overlayState.isOpen;
  }, [overlayState.isOpen, setSearchText, allowsCustomValue]);

  // Open dropdown when user starts typing (unless menuTrigger is 'manual').
  // Intentionally watches only searchText: including overlayState.isOpen would
  // re-fire on close and auto-reopen if the input still has text.
  useEffect(() => {
    if (menuTrigger !== 'manual' && searchText !== '' && !overlayState.isOpen && !isDisabled) {
      openOverlay('input');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  // Spectrum's <ListBox> doesn't expose its own focus management, so we mark the focused option
  // with data-dh-focused for SCSS styling and copy the option's id onto
  // the input's aria-activedescendant.
  useEffect(() => {
    var container = listBoxContainerRef.current;
    if (container == null) {
      var _inputRef$current;
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.removeAttribute('aria-activedescendant');
      return;
    }
    var options = container.querySelectorAll('[role="option"]');
    var focusedSuffix = focusedKey != null ? "-option-".concat(normalizeKey(focusedKey)) : null;
    var focusedOptionId;
    options.forEach(el => {
      var matches = focusedSuffix != null && el.id.endsWith(focusedSuffix);
      if (matches) {
        el.setAttribute('data-dh-focused', 'true');
        el.scrollIntoView({
          block: 'nearest'
        });
        focusedOptionId = el.id;
      } else {
        el.removeAttribute('data-dh-focused');
      }
    });
    var input = inputRef.current;
    if (input != null) {
      if (focusedOptionId != null) {
        input.setAttribute('aria-activedescendant', focusedOptionId);
      } else {
        input.removeAttribute('aria-activedescendant');
      }
    }
  }, [focusedKey, filteredItems, inputRef, listBoxContainerRef]);
  return {
    handleInputKeyDown
  };
}
export default useMultiSelectKeyboard;
//# sourceMappingURL=useMultiSelectKeyboard.js.map